'use client';

/**
 * Canvas comments layer (Yjs-backed). Mount as a child of `<ReactFlow>`.
 * Pins live in the transformed viewport; the thread panel + composer are
 * screen-space cards ANCHORED to the pin (positioned from the live RF
 * transform) so they ride along when the canvas pans / zooms.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { useSearchParams } from 'next/navigation';
import { useReactFlow, useStore as useRfStore, ViewportPortal } from '@xyflow/react';
import { Check, MessageSquarePlus, RotateCcw, Smile, Trash2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkspace } from '@/components/workspace/store';
import {
  presenceColorForUserId,
  useParticipantsMirror,
} from '@/components/workspace/presence';
import {
  COMMENT_EMOJIS,
  useCanvasComments,
  type CanvasCommentsApi,
  type CommentMessage,
  type CommentThread,
} from './use-canvas-comments';
import { useTypingPeers, useTypingPublisher } from './use-comments';
import {
  publishCommentThreads,
  subscribeNewThreadAt,
  subscribeOpenThread,
  subscribePlaceComment,
} from './comments-bridge';

const PANEL_W = 320;
// @mentions are wired end-to-end (autocomplete + notify) but hidden for now
// — flip to true to re-enable the picker, highlighting, and notifications.
const MENTIONS_ENABLED = false;

interface Mentionable {
  userId: string;
  username: string;
  avatarUrl?: string | null;
}

/** POST the resolved mention user ids so the server fans out notifications. */
function notifyMentions(
  workspaceId: string,
  userIds: string[],
  threadId: string,
  snippet: string,
): void {
  if (userIds.length === 0) return;
  void fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/comment-mention`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ userIds, threadId, snippet }),
  }).catch(() => {});
}

/** Map the `@name` tokens in `text` to known member user ids. */
function resolveMentions(text: string, members: Mentionable[]): string[] {
  const byName = new Map(members.map((m) => [m.username.toLowerCase(), m.userId]));
  const ids = new Set<string>();
  for (const m of text.matchAll(/@([\w.\-]+)/g)) {
    const id = byName.get(m[1].toLowerCase());
    if (id) ids.add(id);
  }
  return [...ids];
}

export function CanvasComments() {
  const t = useT();
  const comments = useCanvasComments();
  const rf = useReactFlow();
  const tx = useRfStore((s) => s.transform[0]);
  const ty = useRfStore((s) => s.transform[1]);
  const zoom = useRfStore((s) => s.transform[2]);
  const vw = useRfStore((s) => s.width);
  const vh = useRfStore((s) => s.height);
  const workspace = useWorkspace();
  // The bridge mirror (vs `useParticipants`) is provider-safe — CanvasComments
  // also mounts on the anon canvas, which has no <CanvasYjsProvider>.
  const participants = useParticipantsMirror();
  const mentionables = useMemo<Mentionable[]>(
    () =>
      participants.map((p) => ({
        userId: p.user.userId,
        username: p.user.username,
        avatarUrl: p.user.avatarUrl,
      })),
    [participants],
  );

  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [pending, setPending] = useState<{ x: number; y: number } | null>(null);
  const [placing, setPlacing] = useState(false);

  // Deep link from a mention notification (`/workspace/<id>?comment=<threadId>`):
  // open + pan to that thread once it has synced in, then clean the URL.
  // Reactive (useSearchParams) so it also fires when the user clicks the
  // notification while already viewing this workspace. Each distinct id is
  // handled once via `handledDeepLink`.
  const commentParam = useSearchParams().get('comment');
  const handledDeepLink = useRef<string | null>(null);
  useEffect(() => {
    if (!commentParam || handledDeepLink.current === commentParam) return;
    const t = comments.threads.find((x) => x.id === commentParam);
    if (!t) return;
    handledDeepLink.current = commentParam;
    setOpenThreadId(commentParam);
    rf.setCenter(t.x, t.y, { zoom: rf.getZoom(), duration: 300 });
    const url = new URL(window.location.href);
    url.searchParams.delete('comment');
    window.history.replaceState({}, '', url.toString());
  }, [commentParam, comments.threads, rf]);

  // Mirror thread summaries out to the Collaborate panel.
  const { threads, messagesFor } = comments;
  useEffect(() => {
    publishCommentThreads(
      threads.map((t) => {
        const msgs = messagesFor(t.id);
        return {
          id: t.id,
          authorName: t.authorName,
          resolved: t.resolved,
          createdAt: t.createdAt,
          preview: msgs[0]?.text ?? '',
          count: msgs.length,
        };
      }),
    );
  }, [threads, messagesFor]);
  useEffect(() => () => publishCommentThreads([]), []);

  useEffect(
    () =>
      subscribePlaceComment(() => {
        setPlacing(true);
        setPending(null);
        setOpenThreadId(null);
      }),
    [],
  );
  useEffect(
    () =>
      subscribeNewThreadAt((x, y) => {
        setPending({ x, y });
        setPlacing(false);
        setOpenThreadId(null);
      }),
    [],
  );
  useEffect(
    () =>
      subscribeOpenThread((threadId) => {
        setOpenThreadId(threadId);
        setPending(null);
        const t = comments.threads.find((x) => x.id === threadId);
        if (t) rf.setCenter(t.x, t.y, { zoom: rf.getZoom(), duration: 300 });
      }),
    [comments.threads, rf],
  );

  const placeAt = useCallback(
    (clientX: number, clientY: number) => {
      const { x, y } = rf.screenToFlowPosition({ x: clientX, y: clientY });
      setPending({ x, y });
      setPlacing(false);
    },
    [rf],
  );

  // Screen position for a card anchored to a pin at canvas (fx, fy): ride
  // the live transform and sit just BELOW + right of the pin (the pin
  // balloon extends UP from its tip, so a downward offset clears it with a
  // gap). Flip left near the right edge; clamp on-screen.
  const anchorStyle = useCallback(
    (fx: number, fy: number): { left: number; top: number } => {
      const px = fx * zoom + tx;
      const py = fy * zoom + ty;
      let left = px + 10;
      if (left + PANEL_W > vw - 8) left = px - PANEL_W - 10;
      left = Math.max(8, Math.min(left, Math.max(8, vw - PANEL_W - 8)));
      const top = Math.max(8, Math.min(py + 14, Math.max(8, vh - 360)));
      return { left, top };
    },
    [tx, ty, zoom, vw, vh],
  );

  if (!comments.available) return null;

  return (
    <>
      {placing ? (
        <div
          className="absolute inset-0 z-[6] cursor-crosshair"
          onClick={(e) => placeAt(e.clientX, e.clientY)}
          onContextMenu={(e) => {
            e.preventDefault();
            setPlacing(false);
          }}
          title={t('workspace.comments.clickToPlace')}
        />
      ) : null}

      <ViewportPortal>
        {comments.threads.map((t) => (
          <Pin
            key={t.id}
            thread={t}
            count={comments.messagesFor(t.id).length}
            zoom={zoom || 1}
            active={openThreadId === t.id}
            onClick={() => {
              setOpenThreadId(t.id);
              setPending(null);
            }}
          />
        ))}
        {pending ? <PendingPin x={pending.x} y={pending.y} zoom={zoom || 1} /> : null}
      </ViewportPortal>

      {pending ? (
        <NewThreadComposer
          style={anchorStyle(pending.x, pending.y)}
          mentionables={mentionables}
          onSubmit={(text, mentioned) => {
            const id = comments.createThread(pending.x, pending.y, text);
            setPending(null);
            if (id) {
              setOpenThreadId(id);
              notifyMentions(workspace.id, mentioned, id, text);
            }
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {openThreadId ? (
        <ThreadPanel
          comments={comments}
          threadId={openThreadId}
          workspaceId={workspace.id}
          mentionables={mentionables}
          style={anchorStyle(
            comments.threads.find((t) => t.id === openThreadId)?.x ?? 0,
            comments.threads.find((t) => t.id === openThreadId)?.y ?? 0,
          )}
          onClose={() => setOpenThreadId(null)}
        />
      ) : null}
    </>
  );
}

function pinTransform(zoom: number): string {
  return `translate(-2px, -100%) scale(${1 / zoom})`;
}

function Pin({
  thread,
  count,
  zoom,
  active,
  onClick,
}: {
  thread: CommentThread;
  count: number;
  zoom: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'pointer-events-auto absolute flex h-7 min-w-7 items-center justify-center gap-1 rounded-full rounded-bl-none px-2 text-xs font-semibold text-white shadow-md ring-2 ring-white/70 transition-colors',
        thread.resolved
          ? 'bg-muted-foreground/70'
          : active
            ? 'bg-primary'
            : 'bg-amber-500 hover:bg-amber-600',
      )}
      style={{
        left: thread.x,
        top: thread.y,
        transform: pinTransform(zoom),
        transformOrigin: 'bottom left',
      }}
      title={
        thread.resolved
          ? `Resolved · ${count} comment${count === 1 ? '' : 's'}`
          : `${count} comment${count === 1 ? '' : 's'}`
      }
    >
      {thread.resolved ? <Check className="h-3.5 w-3.5" /> : count}
    </button>
  );
}

function PendingPin({ x, y, zoom }: { x: number; y: number; zoom: number }) {
  return (
    <div
      className="pointer-events-none absolute flex h-7 w-7 items-center justify-center rounded-full rounded-bl-none bg-primary text-white shadow-md ring-2 ring-white/70"
      style={{
        left: x,
        top: y,
        transform: pinTransform(zoom),
        transformOrigin: 'bottom left',
      }}
    >
      <MessageSquarePlus className="h-3.5 w-3.5" />
    </div>
  );
}

/** Small round avatar — image when present, else a colored initial. */
function Avatar({ name, src, color }: { name: string; src?: string | null; color: string }) {
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full text-[10px] font-semibold text-white"
      style={{ backgroundColor: color }}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        name.slice(0, 1).toUpperCase()
      )}
    </span>
  );
}

/** Render comment text with `@mentions` lightly highlighted (when enabled). */
function renderText(text: string): React.ReactNode {
  if (!MENTIONS_ENABLED) return text;
  const parts = text.split(/(@[\w.\-]+)/g);
  return parts.map((p, i) =>
    p.startsWith('@') ? (
      <span key={i} className="font-medium text-primary">
        {p}
      </span>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}

/** Composer with `@mention` autocomplete (matches a mention typed at the end). */
function CommentComposer({
  placeholder,
  submitLabel,
  mentionables,
  onSubmit,
  onCancel,
  onChange,
  autoFocus,
}: {
  placeholder: string;
  submitLabel: string;
  mentionables: Mentionable[];
  onSubmit: (text: string, mentionedUserIds: string[]) => void;
  onCancel?: () => void;
  onChange?: (text: string) => void;
  autoFocus?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  // Grow from one line up to ~5 lines, then scroll.
  const autoGrow = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const mentionQuery = (() => {
    if (!MENTIONS_ENABLED) return null;
    const m = draft.match(/@([\w.\-]*)$/);
    return m ? m[1].toLowerCase() : null;
  })();
  const suggestions =
    mentionQuery !== null
      ? mentionables
          .filter((u) => u.username.toLowerCase().includes(mentionQuery))
          .slice(0, 5)
      : [];

  const set = (text: string) => {
    setDraft(text);
    onChange?.(text);
  };
  const pickMention = (u: Mentionable) => {
    set(draft.replace(/@([\w.\-]*)$/, `@${u.username} `));
    requestAnimationFrame(() => {
      autoGrow(ref.current);
      ref.current?.focus();
    });
  };
  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    onSubmit(text, MENTIONS_ENABLED ? resolveMentions(text, mentionables) : []);
    set('');
    // Reset to one line + keep the input active for the next message.
    requestAnimationFrame(() => {
      if (ref.current) ref.current.style.height = '';
      ref.current?.focus();
    });
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={draft}
        onChange={(e) => {
          set(e.target.value);
          autoGrow(e.target);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && onCancel) onCancel();
          // Enter sends, Shift+Enter inserts a newline.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder={placeholder}
        rows={1}
        className="block w-full resize-none overflow-y-auto rounded-lg border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-foreground/30"
      />
      {suggestions.length > 0 ? (
        <div className="absolute bottom-full left-0 z-10 mb-1 w-56 overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-lg">
          {suggestions.map((u) => (
            <button
              key={u.userId}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pickMention(u);
              }}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm hover:bg-accent"
            >
              <Avatar
                name={u.username}
                src={u.avatarUrl}
                color={presenceColorForUserId(u.userId)}
              />
              <span className="truncate">{u.username}</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="mt-1.5 flex justify-end gap-1.5">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent"
          >
            Cancel
          </button>
        ) : null}
        <button
          type="button"
          onClick={submit}
          disabled={!draft.trim()}
          className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground disabled:opacity-40"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

function NewThreadComposer({
  style,
  mentionables,
  onSubmit,
  onCancel,
}: {
  style: { left: number; top: number };
  mentionables: Mentionable[];
  onSubmit: (text: string, mentionedUserIds: string[]) => void;
  onCancel: () => void;
}) {
  const t = useT();
  return (
    <div
      className="absolute z-50 w-80 rounded-xl border border-border bg-popover p-2 shadow-xl"
      style={style}
    >
      <div className="px-1 pb-1 text-xs font-medium text-muted-foreground">{t('workspace.comments.newComment')}</div>
      <CommentComposer
        placeholder={t('workspace.comments.addComment')}
        submitLabel={t('workspace.comments.comment')}
        mentionables={mentionables}
        onSubmit={onSubmit}
        onCancel={onCancel}
        autoFocus
      />
    </div>
  );
}

function ThreadPanel({
  comments,
  threadId,
  workspaceId,
  mentionables,
  style,
  onClose,
}: {
  comments: CanvasCommentsApi;
  threadId: string;
  workspaceId: string;
  mentionables: Mentionable[];
  style: { left: number; top: number };
  onClose: () => void;
}) {
  const t = useT();
  const thread = comments.threads.find((t) => t.id === threadId);
  const messages = comments.messagesFor(threadId);
  const typingPeers = useTypingPeers(threadId);
  const publishTyping = useTypingPublisher();
  const [reactingFor, setReactingFor] = useState<string | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onDraft = useCallback(
    (text: string) => {
      if (!text) {
        publishTyping(null);
        return;
      }
      publishTyping(threadId);
      if (typingTimer.current) clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => publishTyping(null), 5_000);
    },
    [threadId, publishTyping],
  );
  useEffect(() => () => publishTyping(null), [publishTyping]);

  if (!thread) return null;

  return (
    <div
      className="absolute z-50 flex max-h-[70vh] w-80 flex-col rounded-xl border border-border bg-popover shadow-xl"
      style={style}
    >
      <header className="flex items-center gap-1 border-b border-border/60 px-3 py-2">
        <span className="text-sm font-semibold">Comments</span>
        {thread.resolved ? (
          <span className="rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600">
            Resolved
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => comments.resolveThread(threadId, !thread.resolved)}
            title={thread.resolved ? t('workspace.comments.reopen') : t('workspace.comments.resolve')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {thread.resolved ? <RotateCcw className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('workspace.canvas.node.close')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {messages.map((m: CommentMessage) => {
          const reactions = Object.entries(m.reactions ?? {});
          return (
            <div key={m.id} className="group mb-3 flex items-start gap-2">
              <Avatar
                name={m.authorName}
                src={m.authorAvatar}
                color={presenceColorForUserId(m.authorId)}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 text-xs">
                  <span className="font-semibold text-foreground">{m.authorName}</span>
                  <span className="text-muted-foreground/70">
                    {new Date(m.createdAt).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                  <div className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      onClick={() => setReactingFor(reactingFor === m.id ? null : m.id)}
                      title={t('workspace.comments.react')}
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                      <Smile className="h-3.5 w-3.5" />
                    </button>
                    {m.authorId === comments.myUserId ? (
                      <button
                        type="button"
                        onClick={() => comments.deleteMessage(m.id)}
                        title={t('messages.delete')}
                        className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="mt-0.5 whitespace-pre-wrap break-words text-sm text-foreground/90">
                  {renderText(m.text)}
                </div>

                {reactingFor === m.id ? (
                  <div className="mt-1.5 inline-flex gap-0.5 rounded-lg border border-border bg-background p-0.5 shadow-sm">
                    {COMMENT_EMOJIS.map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => {
                          comments.toggleReaction(m.id, e);
                          setReactingFor(null);
                        }}
                        className="rounded px-1.5 py-0.5 text-base hover:bg-accent"
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                ) : null}

                {reactions.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {reactions.map(([emoji, usersArr]) => {
                      const mine = comments.myUserId ? usersArr.includes(comments.myUserId) : false;
                      return (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => comments.toggleReaction(m.id, emoji)}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors',
                            mine
                              ? 'border-primary/40 bg-primary/10 text-foreground'
                              : 'border-border text-muted-foreground hover:bg-accent',
                          )}
                        >
                          <span>{emoji}</span>
                          <span className="tabular-nums">{usersArr.length}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
        {typingPeers.length > 0 ? (
          <div className="text-xs italic text-muted-foreground">
            {typingPeers.map((p) => p.username).join(', ')} typing…
          </div>
        ) : null}
      </div>

      <footer className="border-t border-border/60 p-2">
        <CommentComposer
          placeholder={t('workspace.comments.replyPlaceholder')}
          submitLabel={t('workspace.comments.reply')}
          mentionables={mentionables}
          onChange={onDraft}
          onSubmit={(text, mentioned) => {
            comments.addReply(threadId, text);
            publishTyping(null);
            notifyMentions(workspaceId, mentioned, threadId, text);
          }}
        />
      </footer>
    </div>
  );
}
